import React from 'react'
import { FileText, Loader, TrendingDown, TrendingUp } from 'lucide-react'
import {
  getInquiryKey,
  getInquiryTypeLabel,
  getStatusMeta,
} from '@/components/molecules/InquiriesTable'
import { cn, formatCurrency, formatDate } from '@/utils'
import { ORG_TD, ORG_TH, OrgEmptyState } from '../OrgPrimitives'
import type { MemberInquiriesBaseProps } from './MemberInquiriesBase'

/**
 * Desktop table of one member's bids and RFQs.
 *
 * Deliberately has no Actions column: a manager may *read* a member's activity
 * but there is no org-scoped endpoint to mutate someone else's inquiry, and the
 * per-user delete on `InquiriesTable` is self-scoped only.
 */
export const MemberInquiriesDesktop: React.FC<MemberInquiriesBaseProps> = ({
  memberLabel,
  items,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10">
        <Loader className="animate-spin text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]" size={20} />
        <span className="font-plus-jakarta text-[14px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          Loading activity...
        </span>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <OrgEmptyState
        icon={FileText}
        title="No inquiries yet"
        description={`${memberLabel} has not created any bids or RFQs.`}
        className="py-8"
      />
    )
  }

  return (
    <div className="overflow-x-auto dark:bg-[rgba(10,10,10,0.2)]">
      <table className="w-full min-w-[1000px] border-collapse">
        <thead>
          <tr className="h-10 border-b border-[rgba(45,41,35,0.12)] dark:border-[#0a0a0a] dark:bg-[rgba(28,28,28,0.32)]">
            <th className={cn(ORG_TH, 'w-[180px]')}>Type</th>
            <th className={cn(ORG_TH, 'w-[260px]')}>Project</th>
            <th className={cn(ORG_TH, 'w-[160px]')}>Amount</th>
            <th className={cn(ORG_TH, 'w-[180px]')}>Valuation</th>
            <th className={cn(ORG_TH, 'w-[160px]')}>Status</th>
            <th className={cn(ORG_TH, 'w-[140px]')}>Date</th>
          </tr>
        </thead>
        <tbody>
          {items.map((inquiry, index) => {
            const statusMeta = getStatusMeta(inquiry)
            const StatusIcon = statusMeta.icon
            const isStriped = index % 2 === 0
            const isBuy = getInquiryTypeLabel(inquiry) === 'Buy'
            const modeLabel = inquiry.inquiryType === 'bid' ? 'Bid' : 'RFQ'

            return (
              <tr
                key={getInquiryKey(inquiry)}
                className={
                  isStriped
                    ? 'bg-[rgba(255,255,255,0.28)] dark:bg-[rgba(28,28,28,0.8)]'
                    : 'bg-transparent'
                }
              >
                <td className={ORG_TD}>
                  <div className="flex items-center gap-2">
                    {isBuy ? (
                      <TrendingUp size={16} className="text-[#1fb845]" />
                    ) : (
                      <TrendingDown size={16} className="text-[#ff540c]" />
                    )}
                    <span>{getInquiryTypeLabel(inquiry)}</span>
                    <span className="inline-flex items-center justify-center bg-[rgba(45,41,35,0.12)] px-1 py-[2px] font-plus-jakarta text-[12px] font-medium leading-[16px] text-[#2d2923] dark:bg-[rgba(253,253,253,0.12)] dark:text-[#fdfdfd]">
                      {modeLabel}
                    </span>
                  </div>
                </td>

                <td className={ORG_TD}>
                  <div className="flex items-center gap-[10px]">
                    {inquiry.logo ? (
                      <img
                        src={inquiry.logo}
                        alt={inquiry.project_name}
                        className="h-[26px] w-[26px] rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[rgba(45,41,35,0.12)] font-plus-jakarta text-[10px] font-semibold text-[#2d2923] dark:bg-[rgba(253,253,253,0.12)] dark:text-[#fdfdfd]">
                        {inquiry.project_name.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <span className="truncate font-medium">{inquiry.project_name}</span>
                  </div>
                </td>

                <td className={ORG_TD}>{formatCurrency(inquiry.tranche_size)}</td>
                <td className={ORG_TD}>{formatCurrency(inquiry.valuation)}</td>

                <td className={ORG_TD}>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 border px-[10px] py-[6px] font-plus-jakarta text-[12px] font-medium leading-[16px]',
                      statusMeta.badgeClass,
                    )}
                  >
                    <StatusIcon size={12} />
                    {statusMeta.label}
                  </span>
                </td>

                <td className={ORG_TD}>{formatDate(inquiry.created_at)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default MemberInquiriesDesktop
